import type { FlashcardReviewInput, FlashcardReviewsResponse } from "@edu/shared";

/**
 * Очередь повторений на устройстве: чистые правила (7.2-7.4
 * `docs/flashcards-design.md`). Хранилище и сеть живут в `review-store.ts` и
 * `review-flush.ts`, решения здесь, под тестами (`review-queue.test.ts`).
 *
 * ⚠️ **Модуль едет в СТАРТОВЫЙ чанк**: досылку зовёт `signOut`, а он в старте.
 * Поэтому отсюда только типы из корневого `@edu/shared`, ни `@edu/shared/flashcards`,
 * ни `ts-fsrs`, ни React (7.4).
 *
 * **Жизнь строки.** Оценка кладётся в очередь в момент нажатия (`sentAt: null`).
 * Строка снимается с ОТПРАВКИ только построчным ответом 200; любой не-2xx
 * оставляет очередь как была (7.3). Но со стола она уходит не сразу: принятая
 * строка остаётся с отметкой `sentAt`, пока кэш колоды на устройстве старше
 * этой отметки. Иначе колода, сохранённая до отправки и открытая потом без сети,
 * показала бы карточки так, будто повторений не было: оценки нет ни в кэше
 * (он старше), ни в очереди (её сняли).
 */

/**
 * Строка очереди. `sentAt`: `null` значит «ещё не доставлена»; число это момент
 * ответа `accepted`; у `duplicate` это `attemptAt`, начало первой попытки:
 * сервер принял строку не раньше него.
 */
export type QueuedReview = FlashcardReviewInput & {
  sentAt: number | null;
  /**
   * Начало ПЕРВОЙ попытки отправить строку. Ответ `duplicate` значит, что
   * сервер принял её в одну из прошлых попыток, то есть не раньше этого
   * момента: он и становится `sentAt`. Нуль у `duplicate` выбросил бы оценку из
   * проекции колоды, прочитанной до той попытки.
   */
  attemptAt?: number | null;
};

/** Повторение в том виде, в каком его кладут поверх серверного состояния. */
export type ReviewForFold = { id: string; cardId: string; rating: 1 | 2 | 3 | 4; reviewedAt: number };

/**
 * Сколько держать доставленную строку ради старого кэша колоды. Двое суток: кэш
 * старше этого всё равно протух по `rev` либо будет перечитан при первой сети.
 */
export const SENT_KEEP_MS = 48 * 3_600_000;

const DB_PREFIX = "edu-flashcards-";

/** База на пользователя (7.2): за одним ноутбуком меняются два человека. */
export function flashcardsDbName(userId: string): string {
  return `${DB_PREFIX}${userId}`;
}

/**
 * Базы карточек ДРУГИХ пользователей этого браузера. Только по своему префиксу:
 * чужое имя на том же домене не наше дело (правило уборки `lesson-db.ts`).
 */
export function foreignFlashcardsDbs(names: readonly (string | undefined)[], userId: string): string[] {
  const own = flashcardsDbName(userId);
  return names.filter((n): n is string => n !== undefined && n.startsWith(DB_PREFIX) && n !== own);
}

/** Недоставленные строки в порядке нажатия: так их и шлём. */
export function pendingReviews(rows: readonly QueuedReview[]): QueuedReview[] {
  return rows
    .filter((r) => r.sentAt === null)
    .sort((a, b) => a.reviewedAt - b.reviewedAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Пачки не больше `max` (потолок ручки `FLASHCARD_REVIEWS_BATCH_MAX`). */
export function batchesOf<T>(rows: readonly T[], max: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < rows.length; i += max) out.push(rows.slice(i, i + max));
  return out;
}

/**
 * Что стало с очередью после ответа 200 (7.3). Построчно: `accepted` отмечается
 * моментом ответа, `duplicate` началом первой попытки, `dropped` уходит совсем (карточку убрали
 * или колоду закрыли, и проекции эта оценка больше не нужна). Строки, о
 * которых ответ молчит, не трогаются: они остаются к следующей досылке.
 *
 * Не-2xx сюда не доходит вовсе: при нём очередь не меняется, и это решает
 * вызывающий, не зовя эту функцию.
 */
export function settleReviews(
  rows: readonly QueuedReview[],
  response: FlashcardReviewsResponse,
  now: number,
): { rows: QueuedReview[]; put: QueuedReview[]; removed: string[] } {
  const status = new Map(response.results.map((r) => [r.id, r.status]));
  const out: QueuedReview[] = [];
  const put: QueuedReview[] = [];
  const removed: string[] = [];
  for (const row of rows) {
    const s = row.sentAt === null ? status.get(row.id) : undefined;
    if (s === undefined) out.push(row);
    else if (s === "dropped") removed.push(row.id);
    else {
      const next = { ...row, sentAt: s === "accepted" ? now : (row.attemptAt ?? now) };
      out.push(next);
      put.push(next);
    }
  }
  return { rows: out, put, removed };
}

/**
 * Что класть поверх колоды, прочитанной с сервера в момент `fetchedAt` (начало
 * запроса). Недоставленное всегда; доставленное, только если сервер принял его
 * ПОЗЖЕ чтения колоды, то есть в прочитанном состоянии его ещё нет.
 */
export function reviewsForProjection(rows: readonly QueuedReview[], fetchedAt: number): ReviewForFold[] {
  return rows
    .filter((r) => r.sentAt === null || r.sentAt > fetchedAt)
    .map((r) => ({ id: r.id, cardId: r.cardId, rating: r.rating, reviewedAt: r.reviewedAt }));
}

/**
 * Отметить начало первой попытки у строк пачки (`attemptAt`). Строки, у
 * которых попытка уже была, не меняются: значение нужно ПЕРВОЕ.
 */
export function markAttempt(batch: readonly QueuedReview[], now: number): QueuedReview[] {
  return batch.map((r) => (r.attemptAt == null ? { ...r, attemptAt: now } : r));
}

/** Тело запроса из строк очереди: только поля контракта. */
export function reviewInputsOf(batch: readonly QueuedReview[]): FlashcardReviewInput[] {
  return batch.map((r) => ({ id: r.id, cardId: r.cardId, rating: r.rating, mode: r.mode, reviewedAt: r.reviewedAt }));
}

/**
 * Можно ли слать пачку: хозяин очереди, взятый в начале круга, всё ещё тот, кто
 * в сессии. Вход другого человека посреди круга (или выход) останавливает
 * досылку: его сессия приписала бы чужие оценки ему.
 */
export function maySend(owner: string | null, current: string | null): boolean {
  return owner !== null && owner === current;
}

/**
 * Что делать с ответом на пачку (7.3).
 *
 * - `keep`: ответа 200 нет (не-2xx, 401, обрыв): очередь не трогается, следующая
 *   попытка по таймеру;
 * - `verify`: ВСЯ непустая пачка пришла `dropped`. Так отвечает сервер, если
 *   сессия уже чужая (в соседней вкладке вошёл другой человек): его карточек
 *   здесь нет. Строки не удаляются, пока не подтверждено, что пользователь тот;
 * - `settle`: разложить ответ построчно (`settleReviews`).
 */
export function flushDecision(
  batch: readonly QueuedReview[],
  result: { ok: true; response: FlashcardReviewsResponse } | { ok: false },
): "keep" | "verify" | "settle" {
  if (!result.ok) return "keep";
  const ids = new Set(batch.map((r) => r.id));
  const mine = result.response.results.filter((r) => ids.has(r.id));
  if (batch.length > 0 && mine.length === batch.length && mine.every((r) => r.status === "dropped")) {
    return "verify";
  }
  return "settle";
}

/**
 * Исход проверки пользователя после «вся пачка dropped» (`flushDecision` даёт
 * `verify`). Три исхода, и ни один не ставит досылку навсегда:
 *
 * - `same`: в сессии хозяин очереди, ответ настоящий, раскладываем его;
 * - `other`: в сессии другой человек (вошёл в соседней вкладке), досылка
 *   останавливается, строки лежат до входа хозяина;
 * - `retry`: проверка не ответила (обрыв, таймаут, 401), как при любом
 *   не-2xx: очередь не трогается, следующая попытка по таймеру.
 */
export function verifyOutcome(
  owner: string,
  me: { ok: true; userId: string } | { ok: false },
): "same" | "other" | "retry" {
  if (!me.ok) return "retry";
  return me.userId === owner ? "same" : "other";
}

/** Доставленные строки, которые уже не нужны ни одному кэшу. */
export function staleSent(rows: readonly QueuedReview[], now: number): string[] {
  return rows.filter((r) => r.sentAt !== null && now - r.sentAt > SENT_KEEP_MS).map((r) => r.id);
}

/**
 * Уборка базы при выходе и чужой базы при входе (7.4): что стереть.
 *
 * ⚠️ **Недоставленная очередь не стирается НИКОГДА**: она уедет при следующем
 * входе того же человека. Стирается кэш колод и доставленные строки (они нужны
 * только кэшу). Сама база удаляется, только когда в ней не осталось
 * недоставленного.
 */
export function cleanupPlan(rows: readonly QueuedReview[]): {
  clearDecks: true;
  dropIds: string[];
  deleteDb: boolean;
} {
  return {
    clearDecks: true,
    dropIds: rows.filter((r) => r.sentAt !== null).map((r) => r.id),
    deleteDb: rows.every((r) => r.sentAt !== null),
  };
}

/** Полночь устройства: `dayStart` у `today` (4.3). */
export function localDayStart(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Идентификатор повторения и запроса. `crypto.randomUUID` есть только в
 * защищённом контексте, поэтому запасной путь через `getRandomValues`, как у
 * телеметрии комнаты. Форма v4 с дефисами: её примут и `FlashcardReviewId`, и
 * `uuid()` у `requestId`.
 */
export function newUuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = ((b[6] as number) & 0x0f) | 0x40;
  b[8] = ((b[8] as number) & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
